import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getApi } from "../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../Utils/headOfLineRole";
import { getFieldValue, getTabFieldMappings } from "../../Utils/tabFieldMapper";
import { useNavigate } from "react-router-dom";
import {
  filterDashboardMarriedAnniversaries,
  getRecentlyAddedMemberNameParts,
  isDashboardInLawMember,
  isDashboardSpouseInLawMember,
  isDashboardUpcomingDeceasedCouple,
  isDashboardUpcomingDeceasedMember,
} from "../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../Utils/dashboardStartOfLineMember";
import { fetchFamilyInAgeOrder } from "../../Utils/dashboardDescendants";
import { fetchDescendantsByAgeCategory } from "../../Utils/dashboardDescAgeGroup";
import { fetchMilestoneWeddingAnniversaries } from "../../Utils/dashboardMilestoneAnniversaries";
import { fetchFamilyHeadsGrandCount } from "../../Utils/dashboardFamilyHeadsGrandCount";
import { fetchBirthdaysByFamilyLines } from "../../Utils/dashboardBirthdaysByFamilyLines";
import { fetchBirthByDay } from "../../Utils/dashboardBirthByDay";
import { fetchFamilyMembersByYearOfBirth } from "../../Utils/dashboardFamilyMembersByYear";
import { getMultiBirthsByGenerationCount } from "../../Utils/dashboardMultiBirths";
import { countAgeMilestonesThisYear } from "../../Utils/dashboardMonthlyAgeMilestones";
import {
  buildMultipleBirthSetSizes,
  getMultipleBirthSuperscriptText,
  getMultipleBirthLegendTags,
  getMultipleBirthTagForSize,
} from "../../Utils/dashboardMultipleBirthSuperscript";
import { formatMsToMMDDYYYY } from "../../Utils/dateUtils";
import { getMemberAgeLabel, getMemberDobTimestamp } from "../../Utils/family-tree/dateAndTime";
import {
  DashboardPhotoHeadLine,
  DashboardMemberNameLabel,
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
} from "./DashboardComponents/DashboardPhotoViewer";
import { blockFamilyTreeNavigationOnMobile } from "../Shared/FeatureNoticeModal";
import {
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
  getDashboardFamilyLineShortHeadName,
} from "../../Utils/dashboardFamilyLineNavigation";
import DashboardMarkerLegend from "./DashboardComponents/DashboardMarkerLegend";
import DashboardControlsSection, {
  DashboardControlsActions,
  DashboardQuickActionsList,
} from "./DashboardComponents/DashboardControlsSection";
import useDashboardControls from "./DashboardComponents/useDashboardControls";
import { countDashboardYearAnniversaries } from "./DashboardComponents/AnniversariesThisWeek";
import { toast } from "react-toastify";
import BirthdaysTodayModal from "./DashboardComponents/BirthdaysTodayModal";
import { registerPushToken } from "../../Services/pushNotifications";

const formatDateToMMDDYYYY = (dateValue) => {
  if (!dateValue) return "N/A";
  if (typeof dateValue === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
    return dateValue;
  }
  const formatted = formatMsToMMDDYYYY(dateValue);
  return formatted || "N/A";
};

const formatFamilyLineColorLabel = (color) => {
  if (!color) return "";
  const trimmed = String(color).trim();
  return /\s+line$/i.test(trimmed) ? trimmed : `${trimmed} Line`;
};

function getDashboardBirthdayAge(member) {
  if (member?.turning_age !== undefined && member?.turning_age !== null && member?.turning_age !== "") {
    const parsed = parseInt(member.turning_age, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  if (member?.age !== undefined && member?.age !== null && member?.age !== "") {
    const parsed = parseInt(member.age, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const dobMs = getMemberDobTimestamp(member);
  if (dobMs !== -1) {
    const dob = new Date(dobMs);
    const currentYear = new Date().getUTCFullYear();
    return currentYear - dob.getUTCFullYear();
  }

  return null;
}

// In-laws are listed without their year of birth, the same rule that keeps the
// age off their rows. Only the "MM/DD/YYYY" the date column opens with is
// trimmed; whatever a tab appends after it (the milestone age) is left alone.
const stripBirthYearFromDateDisplay = (dateDisplay) => {
  if (!dateDisplay || typeof dateDisplay !== "string") return dateDisplay;
  return dateDisplay.replace(/^(\d{1,2}\/\d{1,2})\/\d{4}/, "$1");
};

const renderDashboardFamilyLineColor = (item, familyColorField, headsMap) => {
  const headName = getDashboardFamilyLineShortHeadName(item, headsMap);
  const hex = item?.family_line_hex_code || item?.hex_code;
  const color = item?.family_line_color || item?.color_name;

  if (!headName && !hex && !color) {
    return getFieldValue(item, familyColorField);
  }

  return (
    <DashboardPhotoHeadLine
      name={headName || "Start/Top of Line"}
      hexCode={hex}
      colorName={formatFamilyLineColorLabel(color)}
      member={item}
      headsMap={headsMap}
    />
  );
};

const ANNIVERSARIES_COUNT_CACHE_KEY = "dashboardYearAnniversariesCount";

function readCachedAnniversariesCount() {
  try {
    const cached = JSON.parse(localStorage.getItem(ANNIVERSARIES_COUNT_CACHE_KEY));
    return cached?.treeId === getFamilyTreeIdFromStorage() &&
      Number.isFinite(cached?.count)
      ? cached.count
      : null;
  } catch {
    return null;
  }
}

function writeCachedAnniversariesCount(treeId, count) {
  try {
    localStorage.setItem(
      ANNIVERSARIES_COUNT_CACHE_KEY,
      JSON.stringify({ treeId, count })
    );
  } catch {
    // Storage unavailable: the card just waits for the recount next time.
  }
}

function Dashboard() {
  const [activeTab, setActiveTab] = useState("birthdays");
  const [dashboardData, setDashboardData] = useState(null);
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  // Ids of the roster's in-laws. The Upcoming rows (In Memoriam in particular)
  // ship no is_in_laws flag, so the "*" looks the member up here by id.
  const [rosterInLawIds, setRosterInLawIds] = useState(() => new Set());
  // Multiple-birth set size by member id, read across the whole roster. The
  // Upcoming rows carry no multiple-birth fields, so the tag is looked up here.
  const [rosterMultipleBirthSizes, setRosterMultipleBirthSizes] = useState(
    () => new Map()
  );
  // Starts from the last count this browser saw for the tree: the recount needs
  // the slow statistics-with-details call, and the card would sit blank until it
  // lands. The fresh count replaces it (and the saved copy) when it arrives.
  const [anniversariesThisMonthCount, setAnniversariesThisMonthCount] = useState(
    readCachedAnniversariesCount
  );
  // Derived from the same statistics payload the detail page reads, so the card
  // and the page it opens can never show two different numbers.
  const [multiBirthsCount, setMultiBirthsCount] = useState(null);
  // Same idea for Family by Generation & Age: the card prints the grand total its
  // detail page prints, counted off the same rows the page lists.
  const [descendantsCount, setDescendantsCount] = useState(null);
  // And for Descendant Age Group: the report's own two brackets, read for the
  // card when the statistics block does not name them.
  const [descAgeGroupReport, setDescAgeGroupReport] = useState(null);
  // And for Annual Milestone Wedding Anniversaries, whose total is not in the
  // statistics payload either: it comes off the report's own endpoint.
  const [milestoneAnniversariesCount, setMilestoneAnniversariesCount] = useState(null);
  // And for Milestone Ages this Year: the statistics block counts the in-laws
  // the report leaves out, so the year is counted off the detail rows too.
  const [ageMilestonesCount, setAgeMilestonesCount] = useState(null);
  // And for Family Members (since 2000), which the statistics payload does not
  // count either: it comes off the report's own endpoint.
  const [familyMembersSinceYearCount, setFamilyMembersSinceYearCount] =
    useState(null);
  // And for Family Heads, whose grand total comes off the report's own endpoint.
  const [familyHeadsGrandTotal, setFamilyHeadsGrandTotal] = useState(null);
  // And for Birthdays by Family Lines, off the report's own endpoint too.
  const [birthdaysByFamilyLinesCount, setBirthdaysByFamilyLinesCount] = useState(null);
  // And for Birth Day (1-31, Calendar), off the report's own endpoint too.
  const [birthByDayCount, setBirthByDayCount] = useState(null);
  const navigate = useNavigate();

  // First dashboard visit after login: generate this device's FCM token and
  // save it on the backend. A no-op once the token is in localStorage.
  useEffect(() => {
    registerPushToken();
  }, []);

  const {
    visibleControls,
    hiddenControls,
    isEditing: isCustomizingControls,
    isSaving: isSavingControls,
    startEditing: startCustomizingControls,
    cancelEditing: cancelCustomizingControls,
    saveEditing: saveCustomizedControls,
    addControl,
    removeControl,
    moveControlTo,
    resetDraftToDefault,
    canSave: canSaveControls,
    isGridFull: isControlGridFull,
    requiredControlCount,
  } = useDashboardControls();

  // The tray keeps the catalog's order by default; A-Z is for once it holds
  // enough reports that scanning for one beats remembering where it sits.
  const [trayOrder, setTrayOrder] = useState("default"); // "default" | "alpha"

  const orderedHiddenControls = useMemo(
    () =>
      trayOrder === "alpha"
        ? [...hiddenControls].sort((a, b) => a.label.localeCompare(b.label))
        : hiddenControls,
    [hiddenControls, trayOrder]
  );

  // Measured off the top grid so the Quick Actions cards are exactly as wide as
  // the dashboard cards, even though they sit in a narrower box.
  const [controlCardWidth, setControlCardWidth] = useState(null);

  // Phone only in effect: the footer legend pins to the bottom of the screen,
  // but not while Upcoming Events is still running past the fold — a bar
  // hovering over that list reads as part of it. A zero-height marker at the
  // end of the box says when the list is done; from there down the legend
  // sticks. The desktop legend never sticks, so the flag costs it nothing.
  const upcomingEndRef = useRef(null);
  const [isLegendPinned, setIsLegendPinned] = useState(false);

  useEffect(() => {
    const marker = upcomingEndRef.current;
    if (!marker || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        // Sticky from the moment the marker reaches the viewport, and it stays
        // sticky once the marker has scrolled off the top — `isIntersecting`
        // alone would unpin the legend the moment the list scrolled past.
        const viewportBottom =
          entry.rootBounds?.bottom ??
          (typeof window === "undefined" ? 0 : window.innerHeight);
        setIsLegendPinned(entry.boundingClientRect.top <= viewportBottom);
      },
      { threshold: 0 }
    );

    observer.observe(marker);
    return () => observer.disconnect();
  }, []);

  // Each card reads its own number off the same statistics payload.
  const getControlValue = useCallback(
    (control) =>
      control.getValue({
        statistics: dashboardData?.statistics,
        anniversariesThisMonthCount,
        multiBirthsCount,
        descendantsCount,
        descAgeGroupReport,
        milestoneAnniversariesCount,
        ageMilestonesCount,
        familyMembersSinceYearCount,
        familyHeadsGrandTotal,
        birthdaysByFamilyLinesCount,
        birthByDayCount,
      }),
    [
      dashboardData?.statistics,
      anniversariesThisMonthCount,
      multiBirthsCount,
      descendantsCount,
      descAgeGroupReport,
      milestoneAnniversariesCount,
      ageMilestonesCount,
      familyMembersSinceYearCount,
      familyHeadsGrandTotal,
      birthdaysByFamilyLinesCount,
      birthByDayCount,
    ]
  );

  // Controls whose detail page doesn't exist yet carry no route.
  const handleOpenControl = useCallback(
    (control) => {
      if (!control?.route) return;
      navigate(control.route);
    },
    [navigate]
  );

  const handleSaveCustomizedControls = useCallback(async () => {
    const result = await saveCustomizedControls();
    if (!result || result.source === "unchanged") return;
    toast.success(
      result.source === "api"
        ? "Dashboard layout updated"
        : "Dashboard layout saved on this device"
    );
  }, [saveCustomizedControls]);

  const marriedUpcomingAnniversaries = useMemo(
    () =>
      filterDashboardMarriedAnniversaries(
        dashboardData?.upcoming?.wedding_anniversaries || []
      ),
    [dashboardData?.upcoming?.wedding_anniversaries]
  );

  const fetchDashboardData = async () => {
    try {
      const raw = getFamilyTreeIdFromStorage();
      const familyTreeId =
        raw != null && String(raw).trim() !== "" ? String(raw).trim() : "";
      const response = await getApi(
        `/dashboard?family_tree_id=${encodeURIComponent(familyTreeId)}`
      );

      setDashboardData(response?.data);
    } catch (error) {
      console.log("error in fetchDashboardData : ", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Family by Generation & Age is the one card whose number is not in the
  // statistics payload: it comes off the report's own endpoint, the same call
  // its detail page makes, so the card and the page carry one total.
  useEffect(() => {
    const fetchDescendantsCount = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const report = await fetchFamilyInAgeOrder(family_tree_id);
        setDescendantsCount(report.totalCount);
      } catch (error) {
        console.log("error fetching family in age order count:", error);
      }
    };

    fetchDescendantsCount();
  }, []);

  // Descendant Age Group reads its pair off the statistics block's two counts;
  // the report endpoint's own totals stand in until those land, or for a
  // payload without them (see getDescAgeGroupCounts).
  useEffect(() => {
    const fetchDescAgeGroupReport = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        setDescAgeGroupReport(await fetchDescendantsByAgeCategory(family_tree_id));
      } catch (error) {
        console.log("error fetching descendants by age category count:", error);
      }
    };

    fetchDescAgeGroupReport();
  }, []);

  // Annual Milestone Wedding Anniversaries: the card prints the total its detail
  // page prints, off the same call for the current year.
  useEffect(() => {
    const fetchMilestoneAnniversaries = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const report = await fetchMilestoneWeddingAnniversaries(family_tree_id);
        setMilestoneAnniversariesCount(report.totalCount);
      } catch (error) {
        console.log("error fetching milestone wedding anniversaries count:", error);
      }
    };

    fetchMilestoneAnniversaries();
  }, []);

  // Family Heads: the card prints the grand total its detail page prints, off
  // the same call.
  useEffect(() => {
    const fetchFamilyHeads = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const report = await fetchFamilyHeadsGrandCount(family_tree_id);
        setFamilyHeadsGrandTotal(report.grandTotal);
      } catch (error) {
        console.log("error fetching family heads grand count:", error);
      }
    };

    fetchFamilyHeads();
  }, []);

  // Birthdays by Family Lines: the card prints the total its detail page
  // prints, off the same call.
  useEffect(() => {
    const fetchBirthdaysByLines = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const report = await fetchBirthdaysByFamilyLines(family_tree_id);
        setBirthdaysByFamilyLinesCount(report.totalCount);
      } catch (error) {
        console.log("error fetching birthdays by family lines count:", error);
      }
    };

    fetchBirthdaysByLines();
  }, []);

  // Birth Day (1-31, Calendar): the card prints the total its detail page
  // prints, off the same call.
  useEffect(() => {
    const fetchBirthByDayCount = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const report = await fetchBirthByDay(family_tree_id);
        setBirthByDayCount(report.totalCount);
      } catch (error) {
        console.log("error fetching birth by day count:", error);
      }
    };

    fetchBirthByDayCount();
  }, []);

  // Family Members (since 2000): the card prints the total its detail page
  // prints, off the same call to the report's own endpoint.
  useEffect(() => {
    const fetchFamilyMembersSinceYear = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const report = await fetchFamilyMembersByYearOfBirth(family_tree_id);
        setFamilyMembersSinceYearCount(report.totalCount);
      } catch (error) {
        console.log("error fetching family members since 2000 count:", error);
      }
    };

    fetchFamilyMembersSinceYear();
  }, []);

  useEffect(() => {
    const fetchHeadsMap = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode === 200 && response?.data) {
          const allMembers = extractAllMembersFromStatisticsResponse(response.data);
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              allMembers
            )
          );
          setRosterInLawIds(
            new Set(
              allMembers
                .filter(
                  (member) =>
                    member?.id != null &&
                    isDashboardInLawMember(member) &&
                    !isExplicitDashboardStartOrTopOfLineMember(member)
                )
                .map((member) => String(member.id))
            )
          );
          setRosterMultipleBirthSizes(buildMultipleBirthSetSizes(allMembers));
          setMultiBirthsCount(getMultiBirthsByGenerationCount(response.data));
          setAgeMilestonesCount(countAgeMilestonesThisYear(response.data));
          // The card counts the whole year, the same rows its detail page
          // opens on.
          const yearAnniversariesCount = countDashboardYearAnniversaries(response.data);
          setAnniversariesThisMonthCount(yearAnniversariesCount);
          writeCachedAnniversariesCount(family_tree_id, yearAnniversariesCount);
        }
      } catch (error) {
        console.log("error fetching heads of line map:", error);
      }
    };

    fetchHeadsMap();
  }, []);

  useEffect(() => {
    const onFamilyTreeContextChange = () => fetchDashboardData();
    window.addEventListener("localStorageChange", onFamilyTreeContextChange);
    return () =>
      window.removeEventListener("localStorageChange", onFamilyTreeContextChange);
  }, []);

  const handleMemberClick = (memberId) => {
    if (!memberId) return;
    // Phones get the "tree is being revised" notice instead of the unusable canvas
    if (blockFamilyTreeNavigationOnMobile()) return;
    navigate(`/family-tree?memberId=${memberId}`);
  };

  const handleUpcomingItemClick = (item) => {
    if (activeTab === "events") {
      navigate("/events");
      return;
    }
    handleMemberClick(item?.id);
  };

  // Twin/Trip/… tag for an Upcoming row: the row's own record first, then the
  // roster by id. Anniversary rows carry the couple's primary id and a joint
  // name, and Events rows name a host family, so neither is tagged.
  const getUpcomingMultipleBirthTag = useCallback(
    (item, tab) => {
      if (tab === "anniversaries" || tab === "events") return null;
      return (
        getMultipleBirthSuperscriptText(item) ||
        (item?.id != null
          ? getMultipleBirthTagForSize(rosterMultipleBirthSizes.get(String(item.id)))
          : null)
      );
    },
    [rosterMultipleBirthSizes]
  );

  const renderContent = useMemo(() => {
    let list = [];

    if (activeTab == "birthdays") list = dashboardData?.upcoming?.birthdays || [];
    else if (activeTab == "anniversaries") list = marriedUpcomingAnniversaries;
    else if (activeTab == "events") list = dashboardData?.upcoming?.events || [];
    else if (activeTab == "memorials") list = dashboardData?.upcoming?.memorials || [];
    else if (activeTab == "ages_milestone") list = dashboardData?.upcoming?.ages_milestone || [];

    const mappings = getTabFieldMappings(activeTab);

    return list?.map((item, index) => {
      // Anniversary rows carry the couple's primary id, so the roster lookup
      // only applies to the single-member tabs.
      const isInLawRow =
        isDashboardInLawMember(item) ||
        (activeTab !== "anniversaries" &&
          item?.id != null &&
          rosterInLawIds.has(String(item.id)));
      const showBirthdayAge = activeTab === "birthdays" && !isInLawRow;
      const birthdayYears = showBirthdayAge ? getDashboardBirthdayAge(item) : null;
      // A baby turning 0 — born this year — reads in months, not a bare 0.
      const birthdayAge =
        birthdayYears === 0 ? getMemberAgeLabel(item, 0) : birthdayYears;
      // Same deceased marker the detail pages carry (Heads of Line/Color, Birthdays,
      // In Memoriam). Events rows name a host family, not a member, so they skip it.
      // In Memoriam rows are deceased by definition, whatever the payload carries.
      const showDeceasedAngel =
        activeTab === "memorials" ||
        (activeTab === "birthdays" && isDashboardUpcomingDeceasedMember(item)) ||
        (activeTab === "anniversaries" && isDashboardUpcomingDeceasedCouple(item)) ||
        (activeTab === "ages_milestone" && isDashboardUpcomingDeceasedMember(item));
      // In-law "*", same marker the detail pages use. On the anniversaries tab the
      // couple is a single name string, so one "*" flags a couple with an in-law.
      // The Start/Top of Line exclusion has to be the explicit test: these rows carry
      // no head_first_name/generation, which the inferring test reads as "is the root".
      // Both DOB tabs hide an in-law's birth year. Start/Top of Line members are
      // held out the same way the "*" holds them out.
      const hideInLawBirthYear =
        (activeTab === "birthdays" || activeTab === "ages_milestone") &&
        isInLawRow &&
        !isExplicitDashboardStartOrTopOfLineMember(item);
      const showInLawAsterisk =
        activeTab !== "events" &&
        !isExplicitDashboardStartOrTopOfLineMember(item) &&
        (isInLawRow ||
          (activeTab === "anniversaries" && isDashboardSpouseInLawMember(item)));
      const multipleBirthSup = getUpcomingMultipleBirthTag(item, activeTab);

      return (
      <div className="upcoming-item" key={`${activeTab}-${index}`} style={{ cursor: 'pointer' }} onClick={() => handleUpcomingItemClick(item)}>
        <div className="upcoming-info">
          {/* Name (for events: Host) */}
          <div className="left-txt">
            <span>
              <DashboardNameMarkerGroup>
                <span>{getFieldValue(item, mappings?.name?.field)}</span>
                {showInLawAsterisk && <DashboardInLawAsterisk />}
                {showDeceasedAngel && <DashboardDeceasedAngel />}
                {multipleBirthSup ? (
                  <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
                ) : null}
              </DashboardNameMarkerGroup>
            </span>
          </div>

          {/* For events: Family color, Event Name, Event Date; for others: Date then Family/Color */}
          {activeTab === "events" ? (
            <>
              <div className="left-txt">
                <strong>{mappings?.familyColor?.label}</strong>
                <span>
                  {item?.host_hex_code && item?.host_color_name
                    ? (
                        <span style={{ color: item.host_hex_code }}>
                          {item.host_color_name}
                        </span>
                      )
                    : getFieldValue(item, mappings?.familyColor?.field)}
                </span>
              </div>
              <div className="left-txt">
                <strong>{mappings?.eventName?.label}</strong>
                <span>{getFieldValue(item, mappings?.eventName?.field)}</span>
              </div>
              <div className="left-txt">
                <strong>{mappings?.date?.label}</strong>
                <span>{getFieldValue(item, mappings?.date?.field, mappings?.date?.format)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="left-txt">
                <span>
                  {(() => {
                    const dateDisplay = getFieldValue(
                      item,
                      mappings?.date?.field,
                      mappings?.date?.format
                    );
                    return hideInLawBirthYear
                      ? stripBirthYearFromDateDisplay(dateDisplay)
                      : dateDisplay;
                  })()}
                  {birthdayAge != null ? (
                    <>
                      {" "}
                      <span className="dashboard-upcoming-age-num">({birthdayAge})</span>
                    </>
                  ) : null}
                </span>
              </div>
              <div className="left-txt">
                <span className="dashboard-upcoming-family-line">
                  {renderDashboardFamilyLineColor(item, mappings?.familyColor?.field, headsMap)}
                </span>
              </div>
            </>
          )}

          {/* Theme — only when showExtraField (not used for events now) */}
          {mappings?.showExtraField ? (
            <div className="left-txt">
              <strong>{mappings?.theme?.label}</strong>
              <span>{getFieldValue(item, mappings?.theme?.field)}</span>
            </div>
          ) : null}

          {/* RSVP — only when showRSVP */}
          {mappings?.showRSVP ? (
            <div className="left-txt">
              <strong>No. of RSVPs:</strong> <span>{item?.rsvpCount || "3"}</span>
            </div>
          ) : null}
        </div>
      </div>
    );
    });
  }, [
    activeTab,
    dashboardData?.upcoming?.birthdays,
    marriedUpcomingAnniversaries,
    dashboardData?.upcoming?.memorials,
    dashboardData?.upcoming?.events,
    dashboardData?.upcoming?.ages_milestone,
    headsMap,
    rosterInLawIds,
    getUpcomingMultipleBirthTag,
  ]);

  // One element, rendered in the heading at rest and in the rail beside the
  // tray while editing — the buttons keep their handlers either way.
  const controlsActions = (
    <DashboardControlsActions
      isEditing={isCustomizingControls}
      isSaving={isSavingControls}
      canSave={canSaveControls}
      requiredControlCount={requiredControlCount}
      onStartEditing={startCustomizingControls}
      onCancelEditing={cancelCustomizingControls}
      onSaveEditing={handleSaveCustomizedControls}
      onResetToDefault={resetDraftToDefault}
    />
  );

  return (
    <div className="dashboard-container">
      {/* ======= Top Cards (customizable, iPhone Control Center style) ======= */}
      <DashboardControlsSection
        visibleControls={visibleControls}
        isEditing={isCustomizingControls}
        requiredControlCount={requiredControlCount}
        onOpenControl={handleOpenControl}
        onRemoveControl={removeControl}
        onMoveControlTo={moveControlTo}
        onCardWidthChange={setControlCardWidth}
        getControlValue={getControlValue}
      />

      {/* ======= Bottom Section ======= */}
      {/* Once the card width is known, the reports box hugs its two card
          columns and Upcoming Events / Current Month Additions take the rest. */}
      <div
        className={`dash-main-content${
          controlCardWidth ? " dash-main-content--fit-reports" : ""
        }`}
        style={
          controlCardWidth
            ? { "--dashboard-quick-card-width": `${controlCardWidth}px` }
            : undefined
        }
      >
        {/* ===== Upcoming Section ===== */}
        <div className="dash-box upcoming-box">
          <h3>Upcoming Events</h3>

          <div className="tab-header dashboard-upcoming-tabs">
            <button
              className={activeTab === "birthdays" ? "active" : ""}
              onClick={() => setActiveTab("birthdays")}
            >
              Birthdays ({dashboardData?.upcoming?.birthdays?.length ?? 0})
            </button>
            <button
              className={activeTab === "anniversaries" ? "active" : ""}
              onClick={() => setActiveTab("anniversaries")}
            >
              <span className="dashboard-tab-label dashboard-tab-label--full">
                Wedding Anniv ({marriedUpcomingAnniversaries.length})
              </span>
              <span className="dashboard-tab-label dashboard-tab-label--short">
                Wedding Anniv ({marriedUpcomingAnniversaries.length})
              </span>
            </button>
            {/* Events tab temporarily hidden
            <button
              className={activeTab === "events" ? "active" : ""}
              onClick={() => setActiveTab("events")}
            >
              Events ({dashboardData?.upcoming?.events?.length ?? 0})
            </button>
            */}
            <button
              className={activeTab === "memorials" ? "active" : ""}
              onClick={() => setActiveTab("memorials")}
            >
              <span className="dashboard-tab-label dashboard-tab-label--full">
                In Memoriam ({dashboardData?.upcoming?.memorials?.length ?? 0})
              </span>
              <span className="dashboard-tab-label dashboard-tab-label--short">
                In Memoriam ({dashboardData?.upcoming?.memorials?.length ?? 0})
              </span>
            </button>
          </div>

          <div className="upcoming-list">{renderContent}</div>

          {/* End-of-list marker for the sticky legend — see isLegendPinned. */}
          <div
            ref={upcomingEndRef}
            className="dashboard-upcoming-end-marker"
            aria-hidden="true"
          />
        </div>

        {/* ===== Recently Added Members ===== */}
        <div className="dash-box recent-box">
          <h3 className="dashboard-recent-section-title">
            Current Month Additions to the Family Tree (
            {dashboardData?.recently_added_members?.length ?? 0})
          </h3>

          <div className="upcoming-list-second">
            {
              dashboardData?.recently_added_members?.length > 0 ? (
                dashboardData?.recently_added_members?.map((item, index) => {
                  const { baseName, descendantSpouseName } =
                    getRecentlyAddedMemberNameParts(item);
                  // Explicit root test for the same reason as the Upcoming rows: this
                  // payload ships no head_first_name/generation to infer from.
                  const showInLawAsterisk =
                    isDashboardInLawMember(item) &&
                    !isExplicitDashboardStartOrTopOfLineMember(item);
                  const multipleBirthSup = getUpcomingMultipleBirthTag(item, "birthdays");
                  return (
                  <div 
                    className="upcoming-item" 
                    key={item?.id || index}
                    style={{ cursor: 'pointer' }} 
                    onClick={() => item?.id && handleMemberClick(item.id)}
                  >
                    <div className="upcoming-info">
                      <div className="left-txt">
                        <span>
                          <DashboardNameMarkerGroup>
                          <DashboardMemberNameLabel
                            baseName={baseName}
                            descendantSpouseName={descendantSpouseName}
                            afterBaseName={
                              showInLawAsterisk && descendantSpouseName ? (
                                <DashboardInLawAsterisk />
                              ) : null
                            }
                          />
                          {/* Markers run in the same order as the footer legend
                              reads them: *In-Laws, Deceased, Multiple Births. */}
                          {showInLawAsterisk && !descendantSpouseName && (
                            <DashboardInLawAsterisk />
                          )}
                          {isDashboardUpcomingDeceasedMember(item) && (
                            <DashboardDeceasedAngel />
                          )}
                          {multipleBirthSup ? (
                            <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
                          ) : null}
                          </DashboardNameMarkerGroup>
                        </span>
                      </div>
                      {/* Date before Family Line, the same order as the Upcoming Events rows */}
                      <div className="left-txt">
                        <span>{formatDateToMMDDYYYY(item?.added_date)}</span>
                      </div>
                      <div className="left-txt">
                        <span className="dashboard-upcoming-family-line">
                          {renderDashboardFamilyLineColor(item, null, headsMap) || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
                })
              ) : (
                <div className="text-center dashboard-recent-empty">
                  <p className="dashboard-recent-empty-message">
                    No names recently added to the family tree
                  </p>
                </div>
              )
            }
          </div>
        </div>

        {/* ===== Family Data Reports ===== */}
        <div className="dash-box quick-box">
          <div className="quick-box-header">
            <h3>Additional Family Data Reports</h3>
            {/* Sorting the tray while it is open, where every report is on
                screen at once — at rest it holds only what is off the grid. */}
            {isCustomizingControls && (
              <div
                className="quick-box-sort"
                role="group"
                aria-label="Family Data Reports order"
              >
                <button
                  type="button"
                  className={`quick-box-sort-btn${
                    trayOrder === "default" ? " is-active" : ""
                  }`}
                  aria-pressed={trayOrder === "default"}
                  onClick={() => setTrayOrder("default")}
                  title="The order the reports were added in"
                >
                  Default
                </button>
                <button
                  type="button"
                  className={`quick-box-sort-btn${
                    trayOrder === "alpha" ? " is-active" : ""
                  }`}
                  aria-pressed={trayOrder === "alpha"}
                  onClick={() => setTrayOrder("alpha")}
                  title="Sort the reports by name"
                >
                  A-Z
                </button>
              </div>
            )}
            {/* Customize is easy to miss, so the heading line says what it does.
                Out of edit mode only — the toolbar explains itself once the
                cards are jiggling. */}
            {!isCustomizingControls && (
              <p className="quick-box-note">
                Tap <strong>Customize</strong> to swap a report onto the
                dashboard, take one off, or drag the cards into a new order.
              </p>
            )}
            {/* Customize at rest; Reset / Cancel / Done while editing, on their
                own row under the heading so they are seen without scrolling
                past every card. */}
            {controlsActions}
          </div>
          {/* Doubles as the More Controls tray: whatever the user took off the
              top grid lives here, so removing a card hides the tile without
              losing the page behind it. */}
          <div
            className={`quick-box-body${
              isCustomizingControls ? " quick-box-body--editing" : ""
            }`}
          >
            <DashboardQuickActionsList
              controls={orderedHiddenControls}
              isEditing={isCustomizingControls}
              isGridFull={isControlGridFull}
              cardWidth={controlCardWidth}
              onOpenControl={handleOpenControl}
              onAddControl={addControl}
              getControlValue={getControlValue}
            />
          </div>
        </div>

      </div>

      {/* ===== Legend =====
          One footer for the page rather than one per box: the Upcoming Events and
          Current Month Additions lists both carry the same markers, so a single key
          beneath them reads once instead of twice. Marker notes only — the family
          lines are named in the rows themselves here, so the colour legend the
          detail pages carry would only repeat them. */}
      <div
        className={`dashboard-home-legend in-law-footer${
          isLegendPinned ? " is-pinned" : ""
        }`}
      >
        {/* Both lists tag multiple births, so the key covers the tags in
            Current Month Additions and in every Upcoming tab. */}
        <DashboardMarkerLegend
          multipleBirthTags={getMultipleBirthLegendTags(
            [
              dashboardData?.recently_added_members,
              dashboardData?.upcoming?.birthdays,
              dashboardData?.upcoming?.memorials,
              dashboardData?.upcoming?.ages_milestone,
            ],
            (item) => getUpcomingMultipleBirthTag(item, "birthdays")
          )}
        />
      </div>
      {/* Today's birthdays, once a day — see BirthdaysTodayModal. */}
      <BirthdaysTodayModal headsMap={headsMap} />
    </div>
  );
}

export default Dashboard;
